package com.arena_web.data.repository

import com.arena_web.data.api.ChatApi
import com.arena_web.data.dto.*
import com.arena_web.domain.repositories.ChatRepository

class ChatRepositoryImpl(private val chatApi: ChatApi) : ChatRepository {

    override suspend fun createDirectChat(request: CreateDirectChatRequest): Result<ChatDto> =
        apiCall { chatApi.createDirectChat(request) }

    override suspend fun createGroupChat(request: CreateGroupChatRequest): Result<ChatDto> =
        apiCall { chatApi.createGroupChat(request) }

    override suspend fun getChats(params: Map<String, String>): Result<ChatListPageDto> =
        apiCall { chatApi.getChats(params) }

    override suspend fun getChat(chatId: String): Result<ChatDto> =
        apiCall { chatApi.getChat(chatId) }

    override suspend fun updateGroupChat(chatId: String, request: UpdateGroupChatRequest): Result<ChatDto> =
        apiCall { chatApi.updateGroupChat(chatId, request) }

    // Note: sendMediaMessage deliberately has no retry — it creates a chat
    // message, and a retried request whose first attempt actually landed would
    // duplicate it. uploadChatImage just stores an image, so it retries.
    override suspend fun uploadChatImage(chatId: String, fileBytes: ByteArray, fileName: String, contentType: String): Result<ImageUploadResponse> =
        apiCall { retryOnceOnNetworkError { chatApi.uploadChatImage(chatId, fileBytes, fileName, contentType) } }

    override suspend fun leaveChat(chatId: String): Result<Unit> =
        apiCallUnit { chatApi.leaveChat(chatId) }

    override suspend fun getMessages(chatId: String, params: Map<String, String>): Result<MessagesPageDto> =
        apiCall { chatApi.getMessages(chatId, params) }

    override suspend fun sendMessage(chatId: String, request: SendMessageRequest): Result<MessageDto> =
        apiCall { chatApi.sendMessage(chatId, request) }

    override suspend fun markAsRead(chatId: String): Result<Unit> =
        apiCallUnit { chatApi.markAsRead(chatId) }

    override suspend fun deleteMessage(chatId: String, messageId: String): Result<Unit> =
        apiCallUnit { chatApi.deleteMessage(chatId, messageId) }

    override suspend fun editMessage(chatId: String, messageId: String, request: EditMessageRequest): Result<MessageDto> =
        apiCall { chatApi.editMessage(chatId, messageId, request) }

    override suspend fun sendMediaMessage(chatId: String, fileBytes: ByteArray, fileName: String, fileContentType: String, duration: Int?): Result<MessageDto> =
        apiCall { chatApi.sendMediaMessage(chatId, fileBytes, fileName, fileContentType, duration) }

    override suspend fun addReaction(chatId: String, messageId: String, request: AddReactionRequest): Result<Unit> =
        apiCallUnit { chatApi.addReaction(chatId, messageId, request) }

    override suspend fun removeReaction(chatId: String, messageId: String, emoji: String): Result<Unit> =
        apiCallUnit { chatApi.removeReaction(chatId, messageId, emoji) }

    override suspend fun togglePin(chatId: String, messageId: String): Result<MessageDto> =
        apiCall { chatApi.togglePin(chatId, messageId) }

    override suspend fun getPinnedMessages(chatId: String): Result<List<MessageDto>> =
        apiCall { chatApi.getPinnedMessages(chatId) }

    override suspend fun forwardMessage(chatId: String, messageId: String, request: ForwardMessageRequest): Result<MessageDto> =
        apiCall { chatApi.forwardMessage(chatId, messageId, request) }

    override suspend fun searchMessages(chatId: String, params: Map<String, String>): Result<MessagesPageDto> =
        apiCall { chatApi.searchMessages(chatId, params) }

    override suspend fun kickMember(chatId: String, userId: String): Result<Unit> =
        apiCallUnit { chatApi.kickMember(chatId, userId) }

    override suspend fun inviteToChat(chatId: String, request: InviteToChatRequest): Result<Unit> =
        apiCallUnit { chatApi.inviteToChat(chatId, request) }

    override suspend fun getInvitations(): Result<List<ChatInvitationDto>> =
        apiCall { chatApi.getInvitations() }

    override suspend fun respondToInvitation(invitationId: String, request: RespondToInvitationRequest): Result<Unit> =
        apiCallUnit { chatApi.respondToInvitation(invitationId, request) }

    override suspend fun joinChat(chatId: String): Result<Unit> =
        apiCallUnit { chatApi.joinChat(chatId) }

    override suspend fun getJoinRequests(chatId: String): Result<List<ChatJoinRequestDto>> =
        apiCall { chatApi.getJoinRequests(chatId) }

    override suspend fun respondToJoinRequest(chatId: String, requestId: String, request: RespondToJoinRequestRequest): Result<Unit> =
        apiCallUnit { chatApi.respondToJoinRequest(chatId, requestId, request) }

    override suspend fun muteChat(chatId: String, request: MuteChatRequest): Result<Unit> =
        apiCallUnit { chatApi.muteChat(chatId, request) }

    override suspend fun getOnlineUsers(chatId: String): Result<Set<String>> =
        apiCall<OnlineUsersResponse> { chatApi.getOnlineUsers(chatId) }
            .map { it.onlineUserIds.toSet() }
}
